<script setup lang="ts">
import { ElFormItem } from "element-plus";
import { computed, ref, watch } from "vue";
import { excludeObjectKeys } from "../../../utils";
import { formItemExcludeKeys, FormItemProps } from "./form";

defineOptions({
  name: "IFormItem",
});

const props = defineProps(FormItemProps);
const emits = defineEmits<{
  (e: "change", prop: string, val: any, ref: any, component: string): void;
  (e: "update:modelValue", value: any): void;
  (e: "blur", value: any): void;
}>();

// 计算排除特定键后的属性
const attrs = computed(() => excludeObjectKeys(props, formItemExcludeKeys));

// 动态样式计算
const mergeStyle = computed(() => ({
  gridColumnStart: props.column ? `span ${props.column}` : "span 1",
  gridRowStart: props.row ? `span ${props.row}` : "span 1",
}));

// 将-分割的字符串转换为小写字母开头的驼峰格式
const toCamelCase = (str?: string): string => {
  return str
    ? str.replace(/-([a-z])/g, (_, group1) => group1.toUpperCase())
    : "";
};

// 动态组件属性计算
const componentName = computed(() => toCamelCase(props.comp));
const componentProps = computed(() => {
  const key = componentName.value;
  // @ts-ignore
  return key ? props[key] || {} : {};
});
const componentAttrs = computed(() =>
  excludeObjectKeys(componentProps.value, ["className"]),
);

const componentRef = ref<HTMLElement | null>(null);
const v = ref<any>(props.modelValue);
const formItemRef = ref();

// 监听 modelValue 变化并更新本地状态
watch(
  () => props.modelValue,
  (val) => {
    if (v.value !== val) {
      v.value = val;
    }
  },
  { deep: true, immediate: true },
);

// 处理 change 事件
const handleChange = async (val: any) => {
  emits("update:modelValue", val);
  emits(
    "change",
    props.prop as string,
    val,
    componentRef.value,
    props.comp as string,
  );
  await formItemRef.value?.validate("change");
};
const handleBlur = async (event: Event) => {
  emits("blur", event);
  await formItemRef.value?.validate("blur");
};
</script>

<template>
  <ElFormItem
    ref="formItemRef"
    v-bind="attrs"
    :class="className"
    :style="mergeStyle"
  >
    <slot v-if="comp === 'slot'" :name="prop" />
    <component
      :is="comp"
      v-else
      ref="componentRef"
      v-bind="componentAttrs"
      v-model="v"
      :action="action"
      class="i-form-component"
      :class="componentProps?.className"
      @change="handleChange"
      @blur="handleBlur"
    />
  </ElFormItem>
</template>
